import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { memberCountryService } from '@/lib/api/services/memberCountry'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  MemberCountry,
  CreateMemberCountry,
  UpdateMemberCountry,
  UpdateMemberCountryStatus,
  MemberCountryFilterParams,
  MemberCountryStats,
  MemberCountryStatsResponse,
  MemberCountryRegionStats,
  MemberCountryRegionStatsResponse
} from '@/lib/api/types'


export const memberCountryKeys = {
  all: ['memberCountries'] as const,
  lists: () => [...memberCountryKeys.all, 'list'] as const,
  list: (filters?: MemberCountryFilterParams) => [...memberCountryKeys.lists(), filters] as const,
  details: () => [...memberCountryKeys.all, 'detail'] as const,
  detail: (id: string) => [...memberCountryKeys.details(), id] as const,
  search: (query: string, params?: PaginationParams) => [...memberCountryKeys.all, 'search', query, params] as const,
  stats: () => [...memberCountryKeys.all, 'stats'] as const
}


export function useMemberCountriesStats() {
  return useQuery<MemberCountryStatsResponse, Error, MemberCountryStats>({
    queryKey: [...memberCountryKeys.stats(), 'overview'],
    queryFn: () => memberCountryService.getMemberCountriesStats(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}

export function useMemberCountryRegionStats(regionId?: string) {
  return useQuery<MemberCountryRegionStatsResponse, Error, MemberCountryRegionStats>({
    queryKey: [...memberCountryKeys.stats(), 'region', regionId],
    queryFn: () => memberCountryService.getMemberCountryRegionStats(regionId!),
    select: (response) => response.data,
    enabled: !!regionId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData
  })
}

// export function useAllMemberCountries(filters?: MemberCountryFilterParams) {
//   return useQuery<ApiResponse<PaginatedResponse<MemberCountry>>, Error>({
//     queryKey: memberCountryKeys.list(filters),
//     queryFn: () => memberCountryService.getAllMemberCountries(filters)
//   })
// }
export function useAllMemberCountries() {
  return useQuery<ApiResponse<MemberCountry[]>, Error>({
    queryKey: memberCountryKeys.list(),
    queryFn: () => memberCountryService.getAllMemberCountries()
  })
}


export function useMemberCountry(memberCountryId?: string) {
  return useQuery<ApiResponse<MemberCountry>, Error>({
    queryKey: memberCountryKeys.detail(memberCountryId!),
    queryFn: () => memberCountryService.getMemberCountryById(memberCountryId!),
    enabled: !!memberCountryId
  })
}


export function useCreateMemberCountry() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<MemberCountry>, Error, CreateMemberCountry>({
    mutationFn: (data) => memberCountryService.createMemberCountry(data),
    
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberCountryKeys.lists() })
    }
  })
}


export function useUpdateMemberCountry() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<MemberCountry>,
    Error,
    { memberCountryId: string; data: UpdateMemberCountry }
  >({
    mutationFn: ({ memberCountryId, data }) =>
      memberCountryService.updateMemberCountry(memberCountryId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: memberCountryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: memberCountryKeys.detail(variables.memberCountryId) })
    }
  })
}


export function useUpdateMemberCountryStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { memberCountryId: string; data: UpdateMemberCountryStatus }
  >({
    mutationFn: ({ memberCountryId, data }) =>
      memberCountryService.updateMemberCountryStatus(memberCountryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberCountryKeys.lists() })
    }
  })
}

export function useDeleteMemberCountry() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { memberCountryId: string; data: UpdateMemberCountry }
  >({
    mutationFn: ({ memberCountryId }) =>
      memberCountryService.deleteMemberCountry(memberCountryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: memberCountryKeys.lists() })
    }
  })
}

// Hook to search member countries
export function useSearchMemberCountries(
  params: PaginationParams = { page: 1, size: 10 },
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<MemberCountry>>, Error>({
    queryKey: memberCountryKeys.search(searchBy || '', params),
    queryFn: () => memberCountryService.searchMemberCountries(params, searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to filter member countries
export function useFilterMemberCountries(
  params: PaginationParams,
  filters: MemberCountryFilterParams,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<PaginatedResponse<MemberCountry>>, Error>({
    queryKey: memberCountryKeys.list(filters),
    queryFn: () => memberCountryService.filterMemberCountries(params, filters),
    enabled: enabled && Object.keys(filters).length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}
